import React, { ReactNode } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { getAuthorizedHospitalIds } from '../../context/AuthContext'
import LoadingNotice from '../LoadingNotice/LoadingNotice'

interface RequireAuthProps {
    projectId: string
    children: ReactNode
}

const RequireAuth: React.FC<RequireAuthProps> = ({ projectId, children }) => {
    const auth = useAuth()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    if (auth.loading) {
        return <LoadingNotice title="Checking your session..." subtitle="Please wait" />
    }

    if (!auth.authenticated) {
        return <Navigate to="/login" replace />
    }

    // Wrong (or unsupported) dashboard for this account -- self-heal to where they belong.
    if (!auth.supported || auth.projectId !== projectId) {
        return <Navigate to={auth.supported && auth.defaultPath ? auth.defaultPath : '/login'} replace />
    }

    // Authenticated for the right dashboard, but hasn't completed the Hospital-ID step yet.
    // Practice admins are exempt: their sites come from the username, not a typed ID.
    const isPracticeAdmin = auth.practiceAdminHospitals.length > 0
    if (!auth.hospitalId && !isPracticeAdmin) {
        return <Navigate to="/login" replace />
    }

    // The user's authorized sites are authoritative. A single-site user gets exactly
    // their validated hospital; a practice admin gets all their assigned sites.
    const authorizedIds = getAuthorizedHospitalIds(auth)

    // The URL may carry any non-empty SUBSET of the authorized sites (so a selected
    // subset survives navigation). Anything else -- empty, tampered, or containing a
    // site they aren't granted -- is overridden with the full authorized set.
    const urlParam = searchParams.get('HospitalId')
    const urlIds = urlParam
        ? urlParam.replace(/^\(|\)$/g, '').split(',').map((s) => s.trim()).filter(Boolean)
        : []
    const isAllowed = urlIds.length > 0 && urlIds.every((id) => authorizedIds.includes(id))
    if (!isAllowed) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set('HospitalId', authorizedIds.join(','))
        return <Navigate to={`${location.pathname}?${nextParams.toString()}`} replace />
    }

    return <>{children}</>
}

export default RequireAuth
